import type { ReduxState } from '@/models/types';
import type DataObject from '@/services/dataObject';
import type DatasetViewState from '@/models/objects/datasetViewState';
import type { SqlQueryResults } from '@/services/types';

// A lightweight, screen-agnostic description of "what the user is currently looking at", assembled
// globally (so the summonable AI panel can send it on any route) and serialized into a tagged text
// block that mirrors organizationAIContext's renderContextBlock. It lets the agent resolve
// references like "this dashboard" / "the current dataset" without the user restating context.

export type ScreenContextSnapshot = {
  screenName: string;
  entityType?: string;
  entityName?: string;
  details?: string[];
};

// Path segment (`/:org/:ws/<screen>/...`) → human-readable screen name. Uses the product-facing
// labels (Data Lake/Warehouse) rather than the internal route names.
const SCREEN_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  'gen-dashboard': 'Generative Dashboard',
  'ai-dashboard': 'AI Dashboard',
  hub: 'Warehouse',
  catalog: 'Data Lake',
  insights: 'AI Assistant',
  assistant: 'AI Assistant',
  monitor: 'Monitor',
  flows: 'Flows',
  members: 'Members',
  connections: 'Connections',
  integrations: 'Integrations',
  settings: 'Settings',
};

const MAX_MONITOR_EVENTS = 40;
const MAX_TABLE_ROWS = 10;

// What the table on screen shows, so "the second row" means the row the user sees. SQL has no row
// order of its own: without this the agent answered with whatever row Redshift returned second.
const tableOnScreenDetails = (
  records: SqlQueryResults | null | undefined,
  viewState: DatasetViewState | null | undefined,
): string[] => {
  const details: string[] = [];
  const sortings = viewState?.sortings ?? [];
  if (sortings.length)
    details.push(
      `Sorted by: ${sortings
        .map((s) => `${s.column} ${s.asc ? 'ascending' : 'descending'}`)
        .join(', ')}`,
    );
  const filters = (viewState?.filters ?? []).filter((f) => f.active);
  if (filters.length)
    details.push(
      `Filtered by: ${filters
        .map((f) => `${f.column} ${f.operator} ${JSON.stringify(f.values)}`)
        .join('; ')}`,
    );
  if (records?.length) {
    details.push(
      `The first ${Math.min(
        records.length,
        MAX_TABLE_ROWS,
      )} rows as displayed, in on-screen order (row 1 first). When the user refers to a row by its position, it is one of these:`,
    );
    records
      .slice(0, MAX_TABLE_ROWS)
      .forEach((row, i) => details.push(`${i + 1}. ${JSON.stringify(row).slice(0, 400)}`));
  }
  return details;
};

// Event messages are stored as HTML for the Monitor page; the agent gets plain text.
const stripHtml = (s: string): string =>
  (s || '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// Read a display name off an unknown redux entity without depending on each model's exact type
// (avoids `any` and stays resilient if a field is renamed) — falls back name → title.
const readName = (v: unknown): string | undefined => {
  if (v && typeof v === 'object') {
    const o = v as { name?: unknown; title?: unknown };
    if (typeof o.name === 'string' && o.name.trim()) return o.name.trim();
    if (typeof o.title === 'string' && o.title.trim()) return o.title.trim();
  }
  return undefined;
};

/**
 * The Datalake data object open on screen (`/:org/:ws/catalog/dataobjects/:id`), once loaded.
 * Data objects are not datasets, so the AI panel adds this one to what the agent may query:
 * "summarize this data" has to reach the table the user is looking at, flagged for AI or not.
 */
export const dataObjectOnScreen = (state: ReduxState, pathname: string): DataObject | null => {
  const [, , screen, kind, id] = pathname.split('/').filter(Boolean);
  if (screen !== 'catalog' || kind !== 'dataobjects' || !id) return null;
  const current = state.dataObjects?.current;
  return current && current.id === id ? current : null;
};

/**
 * Build a snapshot of the current screen + the entity in view, from the redux state and the current
 * pathname. Returns null when the path isn't an authenticated app screen. Screen name is always
 * available; entity/details are best-effort per screen.
 */
export const collectScreenContext = (
  state: ReduxState,
  pathname: string,
): ScreenContextSnapshot | null => {
  const segments = pathname.split('/').filter(Boolean); // [org, ws, screen, ...]
  const screen = segments[2];
  if (!screen) return null;

  const snapshot: ScreenContextSnapshot = { screenName: SCREEN_LABELS[screen] ?? screen };
  const details: string[] = [];

  switch (screen) {
    case 'dashboard': {
      const name = readName(state.dashboard?.current);
      if (name) {
        snapshot.entityType = 'dashboard';
        snapshot.entityName = name;
      }
      const widgetTitles = (state.dashboard?.currentWidgets ?? [])
        .map(readName)
        .filter((t): t is string => Boolean(t));
      if (widgetTitles.length) details.push(`Widgets: ${widgetTitles.slice(0, 12).join(', ')}`);
      const filterCount = state.dashboard?.currentFilters?.length ?? 0;
      if (filterCount) details.push(`${filterCount} active filter(s)`);
      break;
    }
    // AI dashboards live under /ai-dashboard/:id; gen-dashboard is the older route name.
    case 'ai-dashboard':
    case 'gen-dashboard': {
      const current = state.generativeDashboard?.current;
      const name = readName(current);
      if (name) {
        snapshot.entityType = 'AI dashboard';
        snapshot.entityName = name;
      }
      const request = current?.prompt?.trim();
      if (request) details.push(`It was generated from the request: "${request.slice(0, 400)}"`);
      break;
    }
    case 'monitor': {
      // What is on screen, newest first, as the page's filters loaded them. For anything else
      // (another period, other filters) the agent has its QueryMonitoringEvents tool.
      const events = state.events?.all ?? [];
      if (events.length) {
        details.push(
          `The ${Math.min(
            events.length,
            MAX_MONITOR_EVENTS,
          )} most recent monitoring events on screen (time, level, action, object and its objectId, message):`,
        );
        events
          .slice(0, MAX_MONITOR_EVENTS)
          .forEach((e) =>
            details.push(
              `- ${new Date(e.actionRecordedAt).toISOString()} | ${e.level} | ${e.actionType} | ${
                e.actionSourceType
              } "${e.actionSourceName}" (objectId ${e.actionSourceId}) | ${stripHtml(
                e.message,
              ).slice(0, 200)}`,
            ),
          );
      } else details.push('No monitoring events are loaded for the current filters.');
      details.push(
        "Use the QueryMonitoringEvents tool for events beyond these. To fix a failed dataset, read it with GetDataset (by the event's objectId) and propose corrected SQL with ProposeDatasetChange. When a failure came from bad data rather than bad SQL, offer a test that catches it next time with ProposeDatasetTest.",
      );
      break;
    }
    case 'hub': {
      const name = readName(state.dataset?.current);
      if (name) {
        snapshot.entityType = 'dataset';
        snapshot.entityName = name;
        details.push(...tableOnScreenDetails(state.dataset?.records, state.dataset?.viewState));
      }
      break;
    }
    case 'catalog': {
      const name = readName(state.dataObjects?.current);
      if (name) {
        snapshot.entityType = 'data object';
        snapshot.entityName = name;
      }
      const onScreen = dataObjectOnScreen(state, pathname);
      if (onScreen) {
        details.push(`SQL table: ${onScreen.dbViewsQualifiedName}`);
        details.push(
          ...tableOnScreenDetails(state.dataObjects?.records, state.dataObjects?.viewState),
        );
      }
      break;
    }
    default:
      break;
  }

  if (details.length) snapshot.details = details;
  return snapshot;
};

/**
 * A stable key for the page the AI panel was summoned on: the entity when the URL names one
 * ("dashboard:<id>", "dataset:<id>"), else the view itself ("screen:monitor"). It tags a panel
 * conversation to its page, so reopening the panel there resumes it and no view's conversation
 * follows the viewer to another. Undefined on the Insights page, which is the full-page chat.
 */
export const screenKeyForPath = (pathname: string): string | undefined => {
  const segments = pathname.split('/').filter(Boolean); // [org, ws, screen, ...rest]
  const [, , screen, a, b] = segments;
  if (!screen || screen === 'assistant' || screen === 'insights') return undefined;
  const entity = (() => {
    switch (screen) {
      case 'dashboard':
        return a ? `dashboard:${a}` : undefined;
      case 'ai-dashboard':
      case 'gen-dashboard':
        return a ? `gen-dashboard:${a}` : undefined;
      case 'hub':
        return a === 'datasets' && b ? `dataset:${b}` : undefined;
      case 'catalog':
        return a === 'dataobjects' && b ? `dataObject:${b}` : undefined;
      case 'flows':
        return a ? `flow:${a}` : undefined;
      default:
        return undefined;
    }
  })();
  return entity ?? `screen:${screen}`;
};

/**
 * Where a conversation was started, in a word for the history list: an i18n id under
 * insights.context, from the key screenKeyForPath gave it. Undefined for the Insights page itself.
 */
export const contextLabelIdOf = (originKey?: string): string | undefined => {
  if (!originKey) return undefined;
  const [kind, rest] = originKey.split(':');
  const view = kind === 'screen' ? rest : kind;
  switch (view) {
    case 'monitor':
      return 'insights.context.monitor';
    case 'catalog':
    case 'dataObject':
      return 'insights.context.datalake';
    case 'hub':
    case 'dataset':
      return 'insights.context.warehouse';
    case 'dashboard':
      return 'insights.context.dashboard';
    case 'ai-dashboard':
    case 'gen-dashboard':
      return 'insights.context.aiDashboard';
    case 'flows':
    case 'flow':
      return 'insights.context.flows';
    case 'home':
      return 'insights.context.home';
    case 'settings':
      return 'insights.context.settings';
    default:
      return 'insights.context.other';
  }
};

/**
 * The contextual call-to-action shown on a new panel conversation, tailored to the type of page the
 * panel was summoned on. Returns an i18n id (the label AND the submitted prompt) plus an emoji icon.
 * Keyed off the screen segment only (no entity needed) so it resolves on any detail page.
 */
export const panelCtaForPath = (pathname: string): { id: string; icon: string } => {
  const screen = pathname.split('/').filter(Boolean)[2];
  switch (screen) {
    case 'dashboard':
    case 'ai-dashboard':
    case 'gen-dashboard':
      return { id: 'insights.panelCta.dashboard', icon: '📊' };
    case 'monitor':
      return { id: 'insights.panelCta.monitor', icon: '🩺' };
    case 'hub':
      return { id: 'insights.panelCta.dataset', icon: '🗂️' };
    case 'catalog':
      return { id: 'insights.panelCta.dataObject', icon: '📄' };
    case 'flows':
      return { id: 'insights.panelCta.flow', icon: '🔀' };
    default:
      return { id: 'insights.panelCta.generic', icon: '✨' };
  }
};

const escapeForBlock = (s: string): string =>
  s.replace(/<\/screen_context>/gi, '').replace(/<\/organization_context>/gi, '');

/**
 * Serialize a screen snapshot into a `<screen_context>` block, mirroring the
 * `<organization_context>` block format so the backend threads it into the prompt the same way.
 */
export const renderScreenContextBlock = (snapshot: ScreenContextSnapshot): string => {
  const lines: string[] = [
    '<screen_context>',
    `The user is currently viewing the "${escapeForBlock(snapshot.screenName)}" screen.`,
  ];
  if (snapshot.entityType && snapshot.entityName) {
    lines.push(
      `Currently open ${escapeForBlock(snapshot.entityType)}: "${escapeForBlock(
        snapshot.entityName,
      )}"`,
    );
  }
  (snapshot.details ?? []).forEach((d) => lines.push(escapeForBlock(d)));
  lines.push(
    'Use this to resolve references to the current screen (e.g. "this dashboard", "the current dataset").',
  );
  lines.push('</screen_context>');
  return lines.join('\n');
};
